import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';

const RealTimeChatContext = createContext();

export const useRealTimeChat = () => {
    const context = useContext(RealTimeChatContext);
    if (!context) {
        throw new Error('useRealTimeChat must be used within a RealTimeChatProvider');
    }
    return context;
};

/**
 * Converts the HTTP base URL from the env variable to a WebSocket URL.
 * - https://my-ngrok-url.ngrok-free.app  -> wss://my-ngrok-url.ngrok-free.app
 * - http://localhost:8000                 -> ws://localhost:8000
 * - REACT_APP_BASE_URL is the SINGLE source of truth. No hardcoded domains.
 */
const getWsBaseUrl = () => {
    let apiBase = process.env.REACT_APP_BASE_URL;
    if (!apiBase) {
        console.error('❌ WS: REACT_APP_BASE_URL is not set in .env. Cannot connect.');
        return null;
    }
    // STRIP QUOTES: Remove any literal " or ' from the .env variable
    apiBase = apiBase.replace(/['"]+/g, '').trim();

    return apiBase.replace(/^https/, 'wss').replace(/^http/, 'ws');
};


export const RealTimeChatProvider = ({ children, isLoggedIn, user }) => {
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [lastMessage, setLastMessage] = useState(null);

    const socketRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const lastConnectTime = useRef(0);
    const listenersRef = useRef(new Set());

    // Keep latest identity in a ref to avoid stale closures inside WS handlers
    const identityRef = useRef({ isLoggedIn, user });
    useEffect(() => {
        identityRef.current = { isLoggedIn, user };
    }, [isLoggedIn, user]);

    const subscribeToMessages = useCallback((callback) => {
        listenersRef.current.add(callback);
        return () => {
            listenersRef.current.delete(callback);
        };
    }, []);


    const connect = useCallback(() => {
        // 1. Check we have a valid environment base URL
        const wsBase = getWsBaseUrl();
        if (!wsBase) return;

        // 2. Check login state and token
        if (!identityRef.current.isLoggedIn) return;
        let token = localStorage.getItem('access_token');
        if (!token) {
            console.log('🔌 WS: No token found, skipping connection.');
            return;
        }

        // 3. Sanitize: strip "Bearer " prefix if present
        token = token.replace(/^Bearer\s+/i, '').trim();

        // 4. Anti-flicker guard: max one attempt every 5s
        const now = Date.now();
        if (now - lastConnectTime.current < 5000) return;

        // 5. Don't open a new socket if one is already open or connecting
        if (socketRef.current) {
            const { readyState } = socketRef.current;
            if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) return;
        }

        lastConnectTime.current = now;

        // Use User ID in path to keep URL short for ngrok
        const u = identityRef.current.user;
        const userId = u?.user_id || u?.id || u?._id || u?.employee_id || u?.email;

        if (!userId) {
            console.warn('🔌 [WS DEBUG] No valid User ID found in profile, skipping connection.');
            setConnectionStatus('disconnected');
            return;
        }

        const wsUrl = `${wsBase}/ws/?token=${token}&ngrok-skip-browser-warning=true`;

        setConnectionStatus('connecting');

        try {
            const ws = new WebSocket(wsUrl);

            socketRef.current = ws;
            window.chatSocket = ws;

            ws.onopen = () => {
                console.log('%c✅ WebSocket Connected', 'color: #10b981; font-weight: bold;');
                setConnectionStatus('connected');
            };


            ws.onmessage = (event) => {
                try {
                    const payload = JSON.parse(event.data);
                    console.log('📡 [WS INCOMING] Payload:', payload); // DEBUG LOG

                    const supportedTypes = [
                        'CHAT_MESSAGE_RECEIVED',
                        'NOTIFICATION_RECEIVED',
                        'RFP_ASSIGNED',
                        'THREAD_READ_STATUS_UPDATED',
                        'NOTIFICATION',
                        'EXTENSION_APPROVED',
                        'EXTENSION_DECISION',
                        'PPT_GENERATION_PROGRESS',
                        'PPT_GENERATION_COMPLETED',
                        'PPT_GENERATION_FAILED',
                        'EXTRACTION_PROGRESS',
                        'EXTRACTION_COMPLETED',
                        'EXTRACTION_FAILED',
                        'TRANSLATION_PROGRESS',
                        'DOWNLOAD_COMPLETE',
                    ];

                    if (payload.event === 'typing' || supportedTypes.includes(payload.type) || String(payload.event).toLowerCase() === 'notification') {
                        // Sanitize: if payload.message is an object (e.g. a full chat message), extract its text so
                        // downstream React renders never receive a raw object as a child.
                        const sanitized = { ...payload };
                        if (sanitized.message !== null && sanitized.message !== undefined && typeof sanitized.message === 'object') {
                            sanitized._rawMessage = sanitized.message; // keep raw on hidden key for hooks that need structured data
                            sanitized.message = sanitized.message.body || sanitized.message.content || sanitized.message.text || sanitized.message.title || '';
                        }
                        
                        const finalPayload = { ...sanitized, received_at: Date.now() };

                        // 1. Notify all direct subscribers (Event Emitter pattern)
                        listenersRef.current.forEach(listener => listener(finalPayload));

                        // 2. ONLY set lastMessage for low-frequency events to avoid global re-render storms
                        const highFrequencyEvents = [
                            'PPT_GENERATION_PROGRESS',
                            'EXTRACTION_PROGRESS',
                            'TRANSLATION_PROGRESS'
                        ];

                        if (!highFrequencyEvents.includes(payload.type)) {
                            setLastMessage(finalPayload);
                        }
                    } else {
                        console.warn('⚠️ [WS] Received unsupported type/event:', payload.type || payload.event);
                    }

                } catch (err) {
                    console.error('❌ [WS] Parse error:', err);
                }
            };


            ws.onclose = (event) => {
                const reason = event.reason || 'No reason provided';
                console.error(`%c⚠️ [WS] DISCONNECTED — Code: ${event.code} | Reason: ${reason}`, 'color: #ef4444; font-weight: bold;');

                setConnectionStatus('disconnected');
                socketRef.current = null;

                if (identityRef.current.isLoggedIn) {
                    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
                    reconnectTimerRef.current = setTimeout(() => connect(), 5000);
                }
            };

            ws.onerror = (err) => {
                // Handled in onclose
            };
        } catch (err) {
            console.error('❌ WS: Failed to create socket:', err);
            setConnectionStatus('disconnected');
        }
    }, []); // Stable — no dependencies; all data read from refs or env

    // Connect when user logs in; cleanup when they log out
    useEffect(() => {
        if (isLoggedIn) {
            connect();
        } else {
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            if (socketRef.current) {
                socketRef.current.close(1000, 'Logout');
                socketRef.current = null;
            }
            setConnectionStatus('disconnected');
        }

        return () => {
            if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
            if (socketRef.current) {
                socketRef.current.close(1000, 'Unmount');
                socketRef.current = null;
            }
        };
    }, [isLoggedIn, connect]);

    const sendJsonMessage = useCallback((msg) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(msg));
            return true;
        }
        return false;
    }, []);

    const value = {
        connectionStatus,
        lastMessage,
        sendJsonMessage,
        subscribeToMessages
    };

    return (
        <RealTimeChatContext.Provider value={value}>
            {children}
        </RealTimeChatContext.Provider>
    );
};
